import {
    PHONE_CHECK_EXP_NAME,
    PHONE_CHECK_STORAGE_NAME,
} from '@/src/modules/checkout/strings'
import { determineIfDateIs24HoursOld } from '@/src/common/utilities/determineIfDateIs24HoursOld'
import hashStringValue from '@/src/modules/checkout/utilities/hashStringValue'
import { isValidPhoneFormatOnlyNumbersAndPlus } from '@/src/modules/build-a-card/validations/schemas'
import { sendTooManyPhoneAttemptsFSEvent } from '@/src/common/utilities/fullstory/events'
import { sendBasicEvent } from '@/src/common/utilities/gtag'
import { PHONE_TOO_MANY_ATTEMPTS } from '@/src/common/constants'
import { User } from 'firebase/auth'

type TLimitPhone = {
    phone: string
    setPhoneError: Function
    user: User | null
}

async function limitByPhoneNumberAttempts({
    phone,
    setPhoneError,
    user,
}: TLimitPhone): Promise<{
    continueCheckout: boolean
}> {
    // verify that phone is valid format before including it as an attempt
    const isPhoneValid: boolean =
        await isValidPhoneFormatOnlyNumbersAndPlus.isValid({
            phone,
        })

    if (!isPhoneValid) {
        setPhoneError('')
        return { continueCheckout: true }
    }

    try {
        const storedPhones: any[] = JSON?.parse(
            window?.localStorage?.getItem(PHONE_CHECK_STORAGE_NAME) ?? '[]'
        )
        const storedExp = window?.localStorage?.getItem(PHONE_CHECK_EXP_NAME)
        const hasBeen24Hours = determineIfDateIs24HoursOld(
            new Date(storedExp ?? '')
        )
        const hashedPhone = hashStringValue(phone)

        // if a new expiration is needed
        if (!storedExp || hasBeen24Hours) {
            // add one to local storage
            const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000)
            window.localStorage.setItem(PHONE_CHECK_EXP_NAME, String(tomorrow))

            // if it has been 24 hours since the last attempt
            // create a new array with the added phone number
            if (hasBeen24Hours) {
                window.localStorage.setItem(
                    PHONE_CHECK_STORAGE_NAME,
                    JSON.stringify([hashedPhone])
                )
                setPhoneError('')
                return { continueCheckout: true }
            }
        }

        // if the array has 3 phones and the current one isn't on there
        // return continueCheckout: false to block the user from continuing
        if (
            storedPhones?.length === 3 &&
            !storedPhones?.includes(hashedPhone)
        ) {
            setPhoneError(
                'Too many attempts to verify phone number. Please try again later.'
            )

            // send FS and datalayer event if blocked
            sendTooManyPhoneAttemptsFSEvent(user, phone, 'failure')
            sendBasicEvent(PHONE_TOO_MANY_ATTEMPTS)

            return { continueCheckout: false }
        }

        // if not blocked and phone number not listed in array, add it
        if (storedPhones?.length < 3 && !storedPhones?.includes(hashedPhone)) {
            storedPhones.push(hashedPhone)

            // and update in local storage
            const phoneString = JSON.stringify(storedPhones)
            window.localStorage.setItem(PHONE_CHECK_STORAGE_NAME, phoneString)
        }

        setPhoneError('')
        return { continueCheckout: true }
    } catch (error) {
        console.error('Ran into an unexpected issue', {
            error: error,
        })
        return { continueCheckout: true }
    }
}

export default limitByPhoneNumberAttempts
